import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Drawer, Button, Typography, Input, Select, Spin } from 'antd';
import { PlusCircleFilled } from '@ant-design/icons';
import debounce from 'lodash/debounce';

import useAxios from './../../../CustomHooks/useAxios';
import ShowCampaignTable from './ShowCampaignTable';
import PageLink from './../../PageLink/PageLink';
import AddCampaign from "./AddCampaign";

function DebounceSelect({ fetchOptions, debounceTimeout = 350, ...props }) {
    const [fetching, setFetching] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const fetchRef = React.useRef(0);

    const debounceFetcher = React.useMemo(() => {
        const loadOptions = (value) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                setOptions(newOptions);
                setFetching(false);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    return (
        <Select
            allowClear
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            {...props}
            options={options}
        />
    );
}

export default function Campaign() {
    let { page } = useParams();
    const { axiosi } = useAxios();
    const user = useSelector((state) => state.loginReducer.user);

    const [visibleAdd, setVisibleAdd] = useState(false);
    const [data, setData] = useState();
    const [refreshSearch, setRefreshSearch] = useState(true);

    const [name, setName] = useState('');
    const [companyid, setCompanyid] = useState(() => {
        try {
            const saved = sessionStorage.getItem('selected_campaign_company');
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });
    const [hideItem, setHideItem] = useState('');

    const isInitialized = React.useRef(false);

    useEffect(() => {
        if (user?.agents?.[0] && !isInitialized.current) {
            if (!companyid) {
                const defaultCompany = { value: user.agents[0].company_id, label: user.agents[0].gmcomname };
                setCompanyid(defaultCompany);
                sessionStorage.setItem('selected_campaign_company', JSON.stringify(defaultCompany));
            }
            if (user.agents[0].company_id !== 1) {
                setHideItem('hideItem');
            } else {
                setHideItem('col');
            }
            isInitialized.current = true;
        }
    }, [user?.agents, companyid]);

    const handleAdd = () => {
        console.log("[handleAdd]:");
        setVisibleAdd(true);
    };

    const onCloseAdd = () => {
        setVisibleAdd(false);
    };

    useEffect(() => {
        doSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSearch, page]);

    const handleSearch = () => {
        console.log("[handleSearch]");
        setRefreshSearch(pre => !pre);
    };

    const handleCompanyChange = (newValue) => {
        setCompanyid(newValue);
        if (newValue) {
            sessionStorage.setItem('selected_campaign_company', JSON.stringify(newValue));
        } else {
            sessionStorage.removeItem('selected_campaign_company');
        }
        setRefreshSearch(pre => !pre);
    };

    const doSearch = async () => {
        console.log("[search]");
        const kq = await axiosi.get('campaigns', {
            params: {
                page,
                pageSize: 20,
                name,
                company_id: companyid ? companyid.value : undefined
            }
        });
        console.log("[handleSearch]:kq.data=", kq.data);
        setData(kq.data);
    };

    async function fetchUserList(company_name) {
        console.log('fetching user', company_name);
        const body = await axiosi.get('companies', { params: { company_name, pageSize: 100 } });
        if (body?.data?.rows) {
            return body?.data?.rows.map((user) => ({
                label: user.company_name,
                value: user.id,
            }));
        } else {
            return [];
        }
    }

    return (<>
        <Drawer title="Add Campaign" placement="right" onClose={onCloseAdd} visible={visibleAdd} width={450}>
            <AddCampaign setVisibleAdd={setVisibleAdd} setRefreshSearch={setRefreshSearch} />
        </Drawer>
        <section className='search'>
            <div className="container">
                <div className="row">
                    <Typography.Title level={3}>Search :
                        <Button onClick={handleAdd} className="addButton" icon={<PlusCircleFilled style={{ color: '#1890ff' }} />}>Add</Button>
                    </Typography.Title>
                </div>
                <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <div className={hideItem} style={{ minWidth: '200px' }}>
                        <label>Company :</label>
                        <DebounceSelect
                            showSearch={true}
                            value={companyid}
                            placeholder="Select company"
                            fetchOptions={fetchUserList}
                            onChange={handleCompanyChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="col" style={{ minWidth: '200px' }}>
                        <label>Campaign name :</label>
                        <Input value={name} onChange={({ target: { value } }) => setName(value)} onPressEnter={handleSearch} />
                    </div>
                    <div className="col" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button type="primary" onClick={handleSearch}>Search</Button>
                    </div>
                </div>
            </div>
        </section>
        <section className='searchResult'>
            <div className="row">
                <Typography.Title level={3}>Result</Typography.Title>
            </div>
            <div className="row">
                {data?.rows && <ShowCampaignTable data={data.rows} setRefreshSearch={setRefreshSearch} />}
            </div>
            <div className="row">
                {data?.pages > 1 ? (
                    <PageLink pageSize={data.pageSize} searchpage={"campaigns"} pages={data.pages} numrows={data.numrows} page={data.page} />
                ) : (
                    data?.rows?.length > 0 && <h1 style={{ fontSize: '1rem', marginTop: '1rem' }}>Only 1 page</h1>
                )}
            </div>
        </section>
    </>);
}
